import { useEffect, useRef, useState, type RefObject } from 'react';

/** Where `el` sits inside `view`, in the page's own (unzoomed) pixels. */
const offsetIn = (el: HTMLElement, view: HTMLElement): { top: number; left: number } => {
  let top = 0;
  let left = 0;
  let node: HTMLElement | null = el;
  while (node && node !== view) {
    top += node.offsetTop;
    left += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }
  return { top, left };
};

/**
 * Brings the ringed item into view inside the drawn window, the way a
 * screenshot is framed: scrolls the page so the item sits near the top of the
 * window, and slides the page left when the item would fall past the right
 * edge of the screen. Returns a number that changes after each move, so the
 * connector can redraw against the new position.
 */
export const useRevealHit = (
  stage: RefObject<HTMLElement | null>,
  hit: RefObject<HTMLElement | null>,
): number => {
  const [moves, setMoves] = useState(0);
  const framed = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const box = stage.current;
    if (!box) return;
    const frame = () => {
      const item = hit.current;
      const view = box.querySelector<HTMLElement>('.bw-view');
      const page = view?.querySelector<HTMLElement>('.bw-page');
      if (!item || !view || !page || framed.current === item) return;
      framed.current = item;
      const { top, left } = offsetIn(item, view);
      view.scrollTop = Math.max(0, top - view.clientHeight * 0.2);
      const zoom = view.getBoundingClientRect().width / view.offsetWidth || 1;
      const visibleRight = (document.documentElement.clientWidth - 24 - view.getBoundingClientRect().left) / zoom;
      const over = left + item.offsetWidth - visibleRight;
      page.style.transform = over > 0 ? `translateX(${-Math.min(over, left - 16)}px)` : '';
      setMoves((n) => n + 1);
    };
    frame();
    const observer = new ResizeObserver(frame);
    observer.observe(box);
    return () => observer.disconnect();
  }, [stage, hit]);
  return moves;
};
